import { Combobox } from "@base-ui/react/combobox";
import { Field } from "@base-ui/react/field";
import { Select as BaseSelect } from "@base-ui/react/select";
import clsx from "clsx";
import type React from "react";
import { forwardRef } from "react";
import { vars } from "../../theme/theme.css";
import { FieldMessage } from "../FieldInfo/FieldMessage";
import { IconChevronDown, IconClose } from "../icons";
import { Spinner } from "../Spinner";
import {
  clearButtonClass,
  comboboxInputClass,
  itemIndicatorClass,
  multiValueBadgeClass,
  noOptionsMessageClass,
  placeholderTextClass,
  selectItemClass,
  selectListClass,
  selectPopupClass,
  selectPositionerClass,
  selectTriggerClass,
  selectValueClass,
  selectWrapperClass,
  triggerIndicatorsContainerClass,
} from "./styles.css";

interface Option {
  label: string;
  value: any;
  isDisabled?: boolean;
}

export interface CustomSelectProps {
  label?: React.ReactNode;
  error?: React.ReactNode;
  info?: React.ReactNode;
  isLoading?: boolean;
  isDisabled?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  options?: Option[];
  containerStyles?: React.CSSProperties;
  className?: string;
  placeholder?: string;
  emptyMessage?: string;
  /**
   * The raw selected value (string, number, etc.) — NOT the Option object.
   * Breaking change from react-select: previously expected an Option object { value, label }.
   */
  value?: any;
  /**
   * Called with the new raw value when the selection changes.
   * Breaking change from react-select: receives the raw value, not a react-select ActionMeta event.
   */
  onChange?: (value: any) => void;
  /** BaseUI-style alias for onChange */
  onValueChange?: (value: any) => void;
  id?: string;
  autoComplete?: string;
  required?: boolean;
}

const RawSelect = forwardRef<HTMLButtonElement, CustomSelectProps>(
  (
    {
      label,
      error,
      info,
      isDisabled,
      isLoading,
      isMulti,
      isClearable,
      isSearchable,
      options = [],
      containerStyles,
      className,
      placeholder = "Selecione",
      emptyMessage = "Nada encontrado",
      value: valueProp,
      onChange,
      onValueChange,
      id,
      autoComplete,
      required,
    },
    ref,
  ) => {
    const currentValue =
      valueProp !== undefined ? valueProp : isMulti ? [] : null;

    const hasValue = isMulti
      ? Array.isArray(currentValue) && currentValue.length > 0
      : currentValue !== null && currentValue !== undefined;

    const handleValueChange = (newValue: any) => {
      onChange?.(newValue);
      onValueChange?.(newValue);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const empty = isMulti ? [] : null;
      handleValueChange(empty);
    };

    const fieldMessage = (
      <FieldMessage
        error={error}
        info={info}
        isDisabled={isDisabled || isLoading}
      />
    );

    if (isSearchable) {
      return (
        <Field.Root
          className={clsx(selectWrapperClass, className)}
          style={containerStyles}
          invalid={!!error}
          data-slot="select-root"
        >
          {label && <Field.Label data-slot="select-label">{label}</Field.Label>}
          <Combobox.Root
            items={options.map((o) => o.value)}
            value={currentValue}
            onValueChange={handleValueChange}
            multiple={isMulti}
            disabled={isDisabled || isLoading}
            itemToStringLabel={(v: any) =>
              options.find((opt) => opt.value === v)?.label ?? String(v ?? "")
            }
            id={id}
          >
            <div style={{ position: "relative", width: "100%" }}>
              <Combobox.Input
                className={comboboxInputClass}
                placeholder={placeholder}
                autoComplete="off"
                style={{ paddingRight: "40px" }}
              />
              <div
                className={triggerIndicatorsContainerClass}
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  height: "100%",
                  pointerEvents: "all",
                }}
              >
                {isClearable && hasValue && (
                  <button
                    type="button"
                    className={clearButtonClass}
                    onClick={handleClear}
                  >
                    <IconClose />
                  </button>
                )}
                {isLoading ? (
                  <Spinner color={vars.colors.neutral.light} size={16} />
                ) : (
                  <IconChevronDown className="custom-dropdown-icon" />
                )}
              </div>
            </div>
            <Combobox.Portal>
              <Combobox.Positioner
                className={selectPositionerClass}
                sideOffset={4}
              >
                <Combobox.Popup className={selectPopupClass}>
                  <Combobox.Empty className={noOptionsMessageClass}>
                    {emptyMessage}
                  </Combobox.Empty>
                  <Combobox.List className={selectListClass}>
                    {(itemValue: any) => {
                      const opt = options.find((o) => o.value === itemValue);
                      if (!opt) return null;
                      return (
                        <Combobox.Item
                          key={opt.value}
                          value={opt.value}
                          disabled={opt.isDisabled}
                          className={selectItemClass}
                        >
                          {opt.label}
                          <Combobox.ItemIndicator
                            className={itemIndicatorClass}
                            keepMounted
                          >
                            ✓
                          </Combobox.ItemIndicator>
                        </Combobox.Item>
                      );
                    }}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Root>
          {fieldMessage}
        </Field.Root>
      );
    }

    return (
      <Field.Root
        className={clsx(selectWrapperClass, className)}
        style={containerStyles}
        invalid={!!error}
        aria-disabled={isDisabled || isLoading}
        data-slot="select-root"
      >
        {label && <Field.Label data-slot="select-label">{label}</Field.Label>}
        <BaseSelect.Root
          value={currentValue}
          onValueChange={handleValueChange}
          multiple={isMulti}
          disabled={isDisabled || isLoading}
          id={id}
          autoComplete={autoComplete}
          required={required}
        >
          <BaseSelect.Trigger
            ref={ref}
            className={selectTriggerClass}
            data-slot="select-trigger"
          >
            <BaseSelect.Value className={selectValueClass}>
              {(value: any) => {
                const isEmpty =
                  value === null ||
                  value === undefined ||
                  (isMulti && Array.isArray(value) && value.length === 0);

                if (isEmpty) {
                  return (
                    <span className={placeholderTextClass}>{placeholder}</span>
                  );
                }

                if (isMulti && Array.isArray(value)) {
                  const selectedOpts = options.filter((opt) =>
                    value.includes(opt.value),
                  );
                  return selectedOpts.map((opt) => (
                    <span key={opt.value} className={multiValueBadgeClass}>
                      {opt.label}
                    </span>
                  ));
                }

                return (
                  options.find((opt) => opt.value === value)?.label ??
                  String(value)
                );
              }}
            </BaseSelect.Value>
            <div className={triggerIndicatorsContainerClass}>
              {isClearable && hasValue && (
                <button
                  type="button"
                  className={clearButtonClass}
                  onClick={handleClear}
                >
                  <IconClose />
                </button>
              )}
              {isLoading ? (
                <Spinner color={vars.colors.neutral.light} size={16} />
              ) : (
                <IconChevronDown className="custom-dropdown-icon" />
              )}
            </div>
          </BaseSelect.Trigger>
          <BaseSelect.Portal>
            <BaseSelect.Positioner
              className={selectPositionerClass}
              sideOffset={4}
              alignItemWithTrigger={false}
            >
              <BaseSelect.Popup
                className={selectPopupClass}
                data-slot="select-popup"
              >
                <BaseSelect.List
                  className={selectListClass}
                  data-slot="select-list"
                >
                  {options.length > 0 ? (
                    options.map((opt) => (
                      <BaseSelect.Item
                        key={opt.value}
                        value={opt.value}
                        disabled={opt.isDisabled}
                        className={selectItemClass}
                      >
                        <BaseSelect.ItemText>{opt.label}</BaseSelect.ItemText>
                        <BaseSelect.ItemIndicator
                          className={itemIndicatorClass}
                          keepMounted
                        >
                          ✓
                        </BaseSelect.ItemIndicator>
                      </BaseSelect.Item>
                    ))
                  ) : (
                    <div className={noOptionsMessageClass}>{emptyMessage}</div>
                  )}
                </BaseSelect.List>
              </BaseSelect.Popup>
            </BaseSelect.Positioner>
          </BaseSelect.Portal>
        </BaseSelect.Root>
        {fieldMessage}
      </Field.Root>
    );
  },
);

export const Select = RawSelect;

RawSelect.displayName = "RawSelect";
